import _jsx from "@babel/runtime/helpers/jsx";
import PropTypes from 'prop-types';
import React, { useState, useEffect } from 'react';
import _get from 'lodash/get';
import createNumberMask from 'text-mask-addons/dist/createNumberMask';
import MaskedTextInput from 'react-text-mask';
import { AccordionPanel, DebouncedInput, ErrorMessages } from 'reaxl';
import FilterTitle from '../FilterTitle';
const stripNonDigits = value => value ? value.toString().replace(/\D/g, '') : null;
const getNumericValue = value => value ? Number.parseInt(stripNonDigits(value), 10) : '';

/**
 *  FilterPriceRange is a custom Filter component that is designed to be used as a Custom Filter type in the Filters Component.
 *  It allows the user to define a minimum and maximum range within a single filter.
 *  It uses the Filters provided onOptionChange callback on selection to handle the updating of any connected reducers
 *
 */

function FilterPriceRange(props) {
  const propMinValue = getNumericValue(_get(props, ['values', 'minPrice'], null));
  const propMaxValue = getNumericValue(_get(props, ['values', 'maxPrice'], null));
  const [state, setState] = useState({
    minPrice: propMinValue,
    maxPrice: propMaxValue,
    errors: undefined
  });
  useEffect(() => {
    setState(prevState => ({
      ...prevState,
      minPrice: propMinValue,
      maxPrice: propMaxValue
    }));
  }, [propMinValue, propMaxValue]);
  const isValid = updatedValues => {
    const {
      minPrice,
      maxPrice
    } = {
      ...state,
      ...updatedValues
    };
    let errors;
    if (minPrice && maxPrice) {
      if (minPrice === maxPrice) {
        return 'Min and Max price cannot be the same.';
      }
      if (minPrice > maxPrice) {
        return 'Max price must be greater than Min price.';
      }
    }
    if (maxPrice === 0) {
      return 'Max price must be greater than 0.';
    }
    return errors;
  };
  const handleBlur = event => {
    // we need to persist the original event to reuse later
    event.persist();
    const value = getNumericValue(event.target.value);

    // Update State with new value if the value has been updated
    if (state[event.target.name] !== value) {
      const errors = isValid({
        [event.target.name]: value
      });
      if (errors) {
        setState(prevState => ({
          ...prevState,
          errors,
          [event.target.name]: null
        }));
        setTimeout(() => {
          event.target.value = '';
        });
      } else {
        const syntheticEvt = {
          ...event
        };
        syntheticEvt.target.value = value;
        setState(prevState => ({
          ...prevState,
          errors: undefined,
          [event.target.name]: value
        }));
        props.onOptionChange(syntheticEvt, props.filters[event.target.name]);
      }
    }
  };
  const handleKeyPress = _ref => {
    let {
      key,
      target
    } = _ref;
    // If Enter key is pressed then we need to blur to kick off the option change
    if (key.toLowerCase() === 'enter') {
      target.blur();
    }
  };
  const {
    filter,
    filters,
    ...rest
  } = props;
  delete rest.collapsed;
  delete rest.collapsedTitle;
  delete rest.filtersOrder;
  delete rest.values;
  delete rest.onOptionChange;
  delete rest.onOptionsClear;
  delete rest.resultCount;
  const title = /*#__PURE__*/_jsx(FilterTitle, {
    title: filter.label
  }, "filterPriceRangeTitleKey");
  const numberMask = createNumberMask({
    prefix: '$',
    includeThousandsSeparator: true,
    allowDecimal: false,
    requireDecimal: false,
    allowLeadingZeroes: false
  });
  const commonInputProps = {
    type: 'tel',
    minLength: '1',
    maxLength: '10',
    pattern: '[0-9,$]{1,10}',
    component: MaskedTextInput,
    mask: numberMask,
    guide: false,
    onBlur: handleBlur,
    onKeyPress: handleKeyPress,
    className: 'form-control'
  };
  const {
    placeholder: minPricePlaceholder = 'Ex:10,000',
    name: minPriceName = 'minPrice',
    label: minPriceLabel = 'Min Price'
  } = _get(filters, 'minPrice', {});
  const {
    placeholder: maxPricePlaceholder = 'Ex:20,000',
    name: maxPriceName = 'maxPrice',
    label: maxPriceLabel = 'Max Price'
  } = _get(filters, 'maxPrice', {});
  return /*#__PURE__*/React.createElement(AccordionPanel, Object.assign({
    "data-cmp": "filterPriceRange",
    eventKey: filter.name,
    title: title
  }, rest), /*#__PURE__*/_jsx("div", {
    className: "row margin-bottom-0"
  }, void 0, /*#__PURE__*/_jsx("div", {
    className: "col-xs-6"
  }, void 0, /*#__PURE__*/React.createElement(DebouncedInput, Object.assign({}, commonInputProps, {
    placeholder: minPricePlaceholder,
    name: minPriceName,
    label: minPriceLabel,
    value: state.minPrice
  }))), /*#__PURE__*/_jsx("div", {
    className: "col-xs-6"
  }, void 0, /*#__PURE__*/React.createElement(DebouncedInput, Object.assign({}, commonInputProps, {
    placeholder: maxPricePlaceholder,
    name: maxPriceName,
    label: maxPriceLabel,
    value: state.maxPrice
  })))), /*#__PURE__*/_jsx(ErrorMessages, {
    errors: state.errors
  }), filter.children);
}
export default FilterPriceRange;